import { Table, TableBody, useTheme, TableHead } from "@mui/material";
import {
  StyledTableCellValueTemplate,
  StyledTableCellLabelTemplate,
  LabelTemplate,
  StyledTableRowTemplate,
  StyledTableContainer,
} from "../../styles/table";
import { Employee } from "../../../types/processTypes";
import { tt } from "../../../../../data/langUtility";
import { formatReferenceNumber } from "../../../utils/referenceNumber";
import { differenceInMonths, isValid } from "date-fns";

interface Props {
  currentEmployee: Employee;
  template: LabelTemplate;
  showHeader?: boolean | false;
  showName?: boolean | false;
  showAgent?: boolean | false;
  showReferenceNumber?: boolean | false;
  showProfession?: boolean | false;
  showSalary?: boolean | false;
  showContractPeriod?: boolean | false;
  showEmployeeTelephone?: boolean | false;
  showContactName?: boolean | false;
  showContactTelephone?: boolean | false;
}

export const BasicPostApplied = ({
  currentEmployee,
  template,
  showHeader,
  showProfession,
  showSalary,
  showContractPeriod,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
}: Props) => {
  const theme = useTheme();
  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        {showHeader && (
          <TableHead>
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("workInformation", "en")}
              </StyledTableCellLabelTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("workInformation", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          </TableHead>
        )}
        <TableBody>
          {showProfession && (
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate template={template}>
                {tt("profession", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  "en",
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  "ar",
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("profession", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showSalary && (
            <StyledTableRowTemplate template={template} key="2">
              <StyledTableCellLabelTemplate template={template}>
                {tt("salary", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.additionalData?.salary?.toLocaleString(
                  theme.direction === "rtl" ? "ar-SA" : "en-US",
                ) +
                  " " +
                  tt(
                    (
                      currentEmployee.additionalData?.currency ?? ""
                    ).toLowerCase(),
                    "en",
                  )}
              </StyledTableCellValueTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.additionalData?.salary?.toLocaleString(
                  theme.direction === "rtl" ? "ar-SA" : "en-US",
                ) +
                  " " +
                  tt(
                    (
                      currentEmployee.additionalData?.currency ?? ""
                    ).toLowerCase(),
                    "ar",
                  )}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("salary", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showContractPeriod && (
            <StyledTableRowTemplate template={template} key="1">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contractPeriod", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  "en",
                )}
              </StyledTableCellValueTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  "ar",
                )}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("contractPeriod", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export const BasicPostAppliedEnglishOnly = ({
  currentEmployee,
  template,
  showHeader,
  showProfession,
  showSalary,
  showContractPeriod,
  showReferenceNumber,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
}: Props) => {
  const theme = useTheme();
  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        {showHeader && (
          <TableHead>
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("workInformation", "en")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          </TableHead>
        )}
        <TableBody>
          {showReferenceNumber && (
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate template={template}>
                {tt("referenceNumber", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {formatReferenceNumber(currentEmployee.referenceNumber)}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
          {showProfession && (
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("profession", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate
                template={template}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  "en",
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
          {showSalary && (
            <StyledTableRowTemplate template={template} key="2">
              <StyledTableCellLabelTemplate template={template}>
                {tt("salary", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.additionalData?.salary?.toLocaleString(
                  theme.direction === "rtl" ? "ar-SA" : "en-US",
                ) +
                  " " +
                  tt(
                    (
                      currentEmployee.additionalData?.currency ?? ""
                    ).toLowerCase(),
                    "en",
                  )}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
          {showContractPeriod && (
            <StyledTableRowTemplate template={template} key="1">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contractPeriod", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  "en",
                )}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export const BasicPostAppliedEnglish = ({
  currentEmployee,
  template,
  showHeader,
  showProfession,
  showSalary,
  showContractPeriod,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
}: Props) => {
  const theme = useTheme();
  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        {showHeader && (
          <TableHead>
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("workInformation", "en")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          </TableHead>
        )}
        <TableBody>
          {showProfession && (
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "left",
                  width: "25%",
                }}
              >
                {tt("profession", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate
                template={template}
                sx={{
                  textAlign: "left",
                  width: "50%",
                }}
              >
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  "en",
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "left",
                  width: "25%",
                }}
              ></StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showSalary && (
            <StyledTableRowTemplate template={template} key="2">
              <StyledTableCellLabelTemplate template={template}>
                {tt("salary", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.additionalData?.salary?.toLocaleString(
                  theme.direction === "rtl" ? "ar-SA" : "en-US",
                ) +
                  " " +
                  tt(
                    (
                      currentEmployee.additionalData?.currency ?? ""
                    ).toLowerCase(),
                    "en",
                  )}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
          {showContractPeriod && (
            <StyledTableRowTemplate template={template} key="1">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contractPeriod", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  "en",
                )}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export const BasicPostAppliedSingleValue = ({
  currentEmployee,
  template,
  showHeader,
  showName,
  showAgent,
  showReferenceNumber,
  showProfession,
  showSalary,
  showContractPeriod,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
}: Props) => {
  const ownerDisplayName = currentEmployee.owner?.displayName ?? "";
  const agentNameSpaceIdx = ownerDisplayName.indexOf(" ");
  const agentNamePrefix =
    agentNameSpaceIdx === -1
      ? ownerDisplayName
      : ownerDisplayName.substring(0, agentNameSpaceIdx + 1);
  const referenceNumber = formatReferenceNumber(
    currentEmployee.referenceNumber,
  );
  const agentYearSuffix = referenceNumber !== "" ? referenceNumber : "";
  const agentCellValue = agentNamePrefix + agentYearSuffix;

  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        {showHeader && (
          <TableHead>
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("workInformation", "en")}
              </StyledTableCellLabelTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("workInformation", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          </TableHead>
        )}
        <TableBody>
          {showAgent && (
            <StyledTableRowTemplate template={template} key="398">
              <StyledTableCellLabelTemplate template={template}>
                {tt("agent", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {agentCellValue}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("agent", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showName && (
            <StyledTableRowTemplate template={template} key="422">
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("fullName", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.givenName + " " + currentEmployee.surName}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("fullName", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showReferenceNumber && (
            <StyledTableRowTemplate template={template} key="378">
              <StyledTableCellLabelTemplate template={template}>
                {tt("referenceNumber", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {formatReferenceNumber(currentEmployee.referenceNumber)}
              </StyledTableCellValueTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("referenceNumber", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showProfession && (
            <StyledTableRowTemplate template={template} key="321">
              <StyledTableCellLabelTemplate template={template}>
                {tt("profession", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  template.cvLang,
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("profession", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showSalary && (
            <StyledTableRowTemplate template={template} key="2">
              <StyledTableCellLabelTemplate template={template}>
                {tt("salary", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.additionalData?.salary?.toLocaleString(
                  template.cvLang === "ar" ? "ar-SA" : "en-US",
                ) +
                  " " +
                  tt(
                    (
                      currentEmployee.additionalData?.currency ?? ""
                    ).toLowerCase(),
                    template.cvLang,
                  )}
              </StyledTableCellValueTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("salary", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showContractPeriod && (
            <StyledTableRowTemplate template={template} key="1">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contractPeriod", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  template.cvLang,
                )}
              </StyledTableCellValueTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("contractPeriod", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showEmployeeTelephone && (
            <StyledTableRowTemplate template={template} key="10">
              <StyledTableCellLabelTemplate template={template}>
                {tt("telephone", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.address
                  ? (currentEmployee.address?.telephone as string)
                  : ""}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("telephone", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showContactName && (
            <StyledTableRowTemplate template={template} key="108">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contactPerson", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.contactPerson
                  ? (currentEmployee.contactPerson?.displayName as string)
                  : ""}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("contactPerson", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showContactTelephone && (
            <StyledTableRowTemplate template={template} key="130">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contactTelephone", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {currentEmployee.contactPerson &&
                currentEmployee.contactPerson.address
                  ? (currentEmployee.contactPerson.address?.telephone as string)
                  : ""}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("contactTelephone", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export const BasicPostAppliedSingleValueGolden = ({
  currentEmployee,
  template,
  showHeader,
}: Props) => {
  const dateOfBirthRaw = currentEmployee.dateOfBirth;
  const dateOfBirthParsed =
    dateOfBirthRaw != null ? new Date(dateOfBirthRaw as Date | string) : null;
  const ageDisplay =
    dateOfBirthParsed != null && isValid(dateOfBirthParsed)
      ? Math.floor(
          differenceInMonths(new Date(), dateOfBirthParsed) / 12,
        ).toLocaleString(template.cvLang === "ar" ? "ar-SA" : "en-US")
      : "";

  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        <TableBody>
          <StyledTableRowTemplate template={template} key="4">
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {tt("fullName", "en")}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{ textAlign: "center", width: "50%" }}
            >
              {currentEmployee.givenName + " " + currentEmployee.surName}
            </StyledTableCellValueTemplate>
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "right",
              }}
            >
              {tt("fullName", "ar")}
            </StyledTableCellLabelTemplate>
          </StyledTableRowTemplate>

          <StyledTableRowTemplate template={template} key="3">
            <StyledTableCellLabelTemplate template={template}>
              {tt("age", "en")}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{ textAlign: "center", width: "50%" }}
            >
              {ageDisplay}
            </StyledTableCellValueTemplate>
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "right",
              }}
            >
              {tt("age", "ar")}
            </StyledTableCellLabelTemplate>
          </StyledTableRowTemplate>

          <StyledTableRowTemplate template={template} key="44">
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "left",
              }}
            >
              {tt("sex", "en")}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{ textAlign: "center", width: "50%" }}
            >
              {tt(currentEmployee.sex as string, "en") +
                " " +
                tt(currentEmployee.sex as string, "ar")}
            </StyledTableCellValueTemplate>
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "right",
              }}
            >
              {tt("sex", "ar")}
            </StyledTableCellLabelTemplate>
          </StyledTableRowTemplate>

          <StyledTableRowTemplate template={template} key="22">
            <StyledTableCellLabelTemplate template={template}>
              {"Monthly Salary"}
            </StyledTableCellLabelTemplate>

            <StyledTableCellValueTemplate
              template={template}
              sx={{ textAlign: "center", width: "50%" }}
            >
              {currentEmployee.additionalData?.salary?.toLocaleString(
                template.cvLang === "ar" ? "ar-SA" : "en-US",
              ) +
                " " +
                tt(
                  (
                    currentEmployee.additionalData?.currency ?? ""
                  ).toLowerCase(),
                  template.cvLang,
                )}
            </StyledTableCellValueTemplate>
            <StyledTableCellLabelTemplate
              template={{ ...template, bodyHeight: 52 }}
              sx={{
                textAlign: "right",
              }}
            >
              {tt("salary", "ar")}
            </StyledTableCellLabelTemplate>
          </StyledTableRowTemplate>
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export const BasicPostAppliedNoLabels = ({
  currentEmployee,
  template,
  showHeader,
  showProfession,
  showSalary,
  showContractPeriod,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
}: Props) => {
  const theme = useTheme();
  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        {showHeader && (
          <TableHead>
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "left",
                }}
              >
                {tt("workInformation", "en")}
              </StyledTableCellLabelTemplate>

              <StyledTableCellLabelTemplate
                template={template}
                colSpan={2}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("workInformation", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          </TableHead>
        )}
        <TableBody>
          {showProfession && (
            <StyledTableRowTemplate template={template} key="3">
              <StyledTableCellLabelTemplate template={template}>
                {tt("profession", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  "en",
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.appliedProfession as string,
                  "ar",
                )
                  .toLowerCase()
                  .toUpperCase()}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("profession", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
          {showSalary && (
            <StyledTableRowTemplate template={template} key="2">
              <StyledTableCellValueTemplate
                template={template}
                sx={{
                  textAlign: "center",
                }}
              >
                {currentEmployee.additionalData?.salary?.toLocaleString(
                  theme.direction === "rtl" ? "ar-SA" : "en-US",
                ) +
                  " " +
                  tt(
                    (
                      currentEmployee.additionalData?.currency ?? ""
                    ).toLowerCase(),
                    "en",
                  )}
              </StyledTableCellValueTemplate>
            </StyledTableRowTemplate>
          )}
          {showContractPeriod && (
            <StyledTableRowTemplate template={template} key="1">
              <StyledTableCellLabelTemplate template={template}>
                {tt("contractPeriod", "en")}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  "en",
                )}
              </StyledTableCellValueTemplate>
              <StyledTableCellValueTemplate template={template}>
                {tt(
                  currentEmployee.additionalData?.contractPeriod as string,
                  "ar",
                )}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("contractPeriod", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export const BasicPostAppliedEnglishMermaid = ({
  currentEmployee,
  template,
}: Props) => {
  const theme = useTheme();
  return (
    <StyledTableContainer
      template={template}
      sx={{
        marginTop: template.spacing,
      }}
    >
      <Table size="small" aria-label="simple table">
        <TableBody>
          <StyledTableRowTemplate template={template} key="3">
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {"Application Code"}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {formatReferenceNumber(currentEmployee.referenceNumber)}
            </StyledTableCellValueTemplate>
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {"Contact Number"}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {currentEmployee?.address?.telephone}
            </StyledTableCellValueTemplate>
          </StyledTableRowTemplate>

          <StyledTableRowTemplate template={template} key="34">
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {"Salary AED"}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {currentEmployee.additionalData?.salary?.toLocaleString(
                theme.direction === "rtl" ? "ar-SA" : "en-US",
              )}
            </StyledTableCellValueTemplate>
            <StyledTableCellLabelTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {"Post Applied"}
            </StyledTableCellLabelTemplate>
            <StyledTableCellValueTemplate
              template={template}
              sx={{
                textAlign: "left",
                width: "25%",
              }}
            >
              {tt(
                currentEmployee.additionalData?.appliedProfession as string,
                "en",
              )
                .toLowerCase()
                .toUpperCase()}
            </StyledTableCellValueTemplate>
          </StyledTableRowTemplate>
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};
